import { MoreVertical, PawPrint } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { NavLink } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { TableCell, TableRow } from '../../../components/ui/Table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../../components/ui/Tooltip';
import { cn } from '../../../lib/utils';
import useResponsive from '../../../hooks/useResponsive';
import { CalendarReservationContentMode } from '../../../models/owner-portal/calendarEvents';
import {
  EditReservationFormI,
  ReservationGeneral
} from '../../../models/owner-portal/reservationGeneral';
import { PATH_HOME } from '../../../routes/paths';
import { fCurrency } from '../../../utils/formatNumber';
import { FDateFormat, fDate } from '../../../utils/formatTime';
import { ReservationStage } from '../../../utils/owner-portal/reservationConstants';
import { useReservationPopoverState } from '../../dashboard/reservation/list/useReservationPopoverState';
import ReservationDetailsPopover from '../../popover/reservation/ReservationDetailsPopover';
import { ReservationOwnerDialogsProvider } from './ReservationOwnerDialogsContext';
import { ReservationTableRowMenu } from './ReservationTableRowMenu';

// ----------------------------------------------------------------------

type Props = {
  row: ReservationGeneral;
  selected: boolean;
  onSelectRow: VoidFunction;
  onDeleteRow: VoidFunction;
  mode: CalendarReservationContentMode;
  dense?: boolean;
  showColumns: Set<keyof ReservationGeneral | ''>;
};

export default function ReservationTableRow({
  row,
  selected,
  onSelectRow: _onSelectRow,
  onDeleteRow: _onDeleteRow,
  mode,
  dense: _dense = true,
  showColumns
}: Props) {
  const {
    name,
    display_guest_type,
    check_in_ISO,
    check_out_ISO,
    nights,
    nightly_rate,
    rental_amount,
    booking_date_ISO,
    guest_id,
    id,
    unit_id,
    listing_id,
    ui_table_guest_name
  } = row;

  const isMobile = useResponsive('down', 'sm');

  const popoverState = useReservationPopoverState();

  const methods = useForm<EditReservationFormI>({
    defaultValues: {
      id,
      guest_id,
      listing_id,
      check_in: check_in_ISO,
      check_out: check_out_ISO
    }
  });

  return (
    <ReservationOwnerDialogsProvider reservation={row} methods={methods} {...popoverState}>
    <TableRow
      className={cn(selected && 'bg-muted')}
      data-state={selected ? 'selected' : undefined}
    >
      {showColumns.has('name') && (
        <TableCell className="min-w-0 overflow-hidden">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => popoverState.setSelectedRow(e.currentTarget)}
              className={cn(
                'text-sm font-medium text-primary dark:text-secondary hover:underline cursor-pointer bg-transparent border-none p-0 text-left block min-w-0 whitespace-nowrap',
                isMobile && 'truncate max-w-[80px] sm:max-w-none'
              )}
            >
              {name}
            </button>
            {row.pet_count > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PawPrint className="h-4 w-4 flex-shrink-0 text-muted-foreground fill-current" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This reservation includes {row.pet_count} {row.pet_count === 1 ? 'pet' : 'pets'}.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </TableCell>
      )}

      {/* TODO: DOUBLE CHECK THESE MODES */}
      {mode === CalendarReservationContentMode.All && showColumns.has('unit_id') && (
        <TableCell>
          <NavLink
            to={PATH_HOME.property.view(listing_id)}
            className="text-sm font-medium text-primary dark:text-secondary hover:underline"
          >
            {unit_id}
          </NavLink>
        </TableCell>
      )}

      {showColumns.has('ui_table_guest_name') && (
        <TableCell>
          <span className="text-sm font-medium">{ui_table_guest_name}</span>
        </TableCell>
      )}

      {showColumns.has('guest_type') && <TableCell>{display_guest_type}</TableCell>}

      {showColumns.has('check_in_ISO') && (
        <TableCell>
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {`${fDate(check_in_ISO, FDateFormat.localized_us_short_month_day)} - ${fDate(
                check_out_ISO,
                FDateFormat.localized_us_short_month_day_year
              )}`}
            </span>
            <span className="text-sm text-muted-foreground">
              {nights} night{nights === 1 ? '' : 's'}
            </span>
          </div>
        </TableCell>
      )}

      {showColumns.has('rental_amount') && (
        <TableCell>
          <div className="flex flex-col">
            <span className="text-sm font-medium">{fCurrency(nightly_rate)}/night</span>
            <span className="text-sm text-muted-foreground">{fCurrency(rental_amount)} total</span>
          </div>
        </TableCell>
      )}

      {showColumns.has('booking_date_ISO') && (
        <TableCell>
          {fDate(booking_date_ISO, FDateFormat.localized_us_numeric_month_day_year)}
        </TableCell>
      )}

      {showColumns.has('') && (
        <TableCell className="text-right">
          <ReservationDetailsPopover popoverState={popoverState} reservation={row} />
          {row.stage_mapped !== ReservationStage.Canceled && (
            <ReservationTableRowMenu
              reservation={row}
              {...popoverState}
              trigger={
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={popoverState.handleOpenPopover}
                  aria-label="Reservation actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              }
            />
          )}
        </TableCell>
      )}
    </TableRow>
    </ReservationOwnerDialogsProvider>
  );
}
